import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";

import Dashboard from "../../Components/Dashboard";
import Denied from "../../Components/Denied";
import { apiLabs } from "../../Services/api";
import history from "../../Services/history";
import { store } from "../../store";

import goldCup from "../../Assets/img/gold-cup.png";
import silverCup from "../../Assets/img/silver-cup.png";
import bronzeCup from "../../Assets/img/bronze-cup.png";

import {
  globalCSS,
  page,
  dash,
  header,
  upload,
  meta,
  kpis,
  podium,
  tableSection,
  summary,
  comparison,
  compareUpload,
  colors,
} from "./style";

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────
const MEDALS = { 1: goldCup, 2: silverCup, 3: bronzeCup };
const PLACE_LABELS = { 1: "1º LUGAR", 2: "2º LUGAR", 3: "3º LUGAR" };

// Colunas conhecidas do relatório (label do XLS → chave interna)
const COLUMN_MAP = [
  { key: "operadorRaw", patterns: ["operador"] },
  { key: "aberturas", patterns: ["abertura"], type: "int" },
  { key: "tempoVenda", patterns: ["tempo venda"], type: "time" },
  { key: "tempoRegistro", patterns: ["tempo registro"], type: "time" },
  { key: "tempoPagto", patterns: ["tempo pag", "tempo pagto"], type: "time" },
  { key: "tempoPausa", patterns: ["tempo pausa"], type: "time" },
  { key: "qtdClientes", patterns: ["qtd cliente", "clientes"], type: "int" },
  { key: "valorTotal", patterns: ["valor total"], type: "decimal" },
  { key: "nroItens", patterns: ["nro itens", "qtd itens", "n itens"], type: "int" },
  { key: "ticketMedio", patterns: ["ticket"], type: "decimal" },
  { key: "minVendido", patterns: ["min", "mín", "minimo", "mínimo"], type: "decimal" },
  { key: "maxVendido", patterns: ["max", "máx", "maximo", "máximo"], type: "decimal" },
  { key: "mediaItens", patterns: ["media itens", "média itens"], type: "decimal" },
  { key: "regMin", patterns: ["reg/min", "reg / min", "reg min"], type: "decimal" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Pontuação do ranking
// ─────────────────────────────────────────────────────────────────────────────
// Cada item registrado vale 1 ponto e cada cliente atendido vale 2 — atender
// mais gente pesa mais do que passar mais peças do mesmo cupom.
// Ex.: 27.000 itens e 3.000 clientes → 27.000 + (3.000 × 2) = 33.000 pontos.
const PONTOS_POR_ITEM = 1;
const PONTOS_POR_CLIENTE = 2;

function calcularPontuacao(nroItens, qtdClientes) {
  return (nroItens || 0) * PONTOS_POR_ITEM + (qtdClientes || 0) * PONTOS_POR_CLIENTE;
}

// ─────────────────────────────────────────────────────────────────────────────
// Formatadores
// ─────────────────────────────────────────────────────────────────────────────
const fmtCurrency = (n) =>
  Number(n || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const MASKED_CURRENCY = "R$ ••••••";
const maskCurrency = () => MASKED_CURRENCY;

// ─────────────────────────────────────────────────────────────────────────────
// Ícones (inline SVG)
// ─────────────────────────────────────────────────────────────────────────────
function EyeIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

const fmtNumber = (n) => Number(n || 0).toLocaleString("pt-BR");

const fmtDecimal = (n, digits = 2) =>
  Number(n || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const pad2 = (n) => String(n).padStart(2, "0");

function fmtSeconds(secs) {
  if (!secs || isNaN(secs)) return "00:00:00";
  const total = Math.max(0, Math.round(Number(secs)));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
}

// Formato HH:MM:SS com sinal preservado (para deltas de tempo na comparação).
function fmtSignedSeconds(secs) {
  if (!secs || isNaN(secs)) return "00:00:00";
  const sign = secs < 0 ? "-" : "";
  return sign + fmtSeconds(Math.abs(secs));
}

// ─────────────────────────────────────────────────────────────────────────────
// Parsers do XLS
// ─────────────────────────────────────────────────────────────────────────────
function parseDecimal(val) {
  if (val == null || val === "") return 0;
  if (typeof val === "number") return val;

  const s = String(val).trim().replace(/\s/g, "").replace(/[R$]/g, "");
  if (!s) return 0;

  const hasComma = s.includes(",");
  const hasDot = s.includes(".");

  let normalized;
  if (hasComma && hasDot) {
    // Ambos os separadores presentes — o que vier por ÚLTIMO é o decimal.
    //   "1.234,56" → vírgula é decimal (BR) → strip pontos, comma → ponto
    //   "1,234.56" → ponto é decimal (inglês) → strip vírgulas
    if (s.lastIndexOf(",") > s.lastIndexOf(".")) {
      normalized = s.replace(/\./g, "").replace(",", ".");
    } else {
      normalized = s.replace(/,/g, "");
    }
  } else if (hasComma) {
    // só vírgula → decimal BR ("321,9" → 321.9)
    normalized = s.replace(",", ".");
  } else if (hasDot) {
    // só ponto → ambíguo. Heurística: se houver mais de um ponto OU se o último
    // grupo após o ponto tiver exatamente 3 dígitos, é separador de milhar BR.
    // Caso contrário, é decimal inglês.
    const dotCount = (s.match(/\./g) || []).length;
    const lastGroup = s.split(".").pop();
    if (dotCount > 1 || lastGroup.length === 3) {
      normalized = s.replace(/\./g, ""); // "1.234" → 1234
    } else {
      normalized = s; // "321.9" ou "321.90" → ponto é decimal
    }
  } else {
    normalized = s;
  }

  const n = parseFloat(normalized);
  return isNaN(n) ? 0 : n;
}

function parseInteger(val) {
  return Math.round(parseDecimal(val));
}

function parseTime(val) {
  if (val == null || val === "") return 0;
  if (typeof val === "number") {
    // Excel time fraction: 1.0 = 1 dia = 86400 segundos
    return Math.round(val * 86400);
  }
  const s = String(val).trim();
  const m = s.match(/(\d+):(\d+):(\d+)/);
  if (m) {
    return (
      parseInt(m[1], 10) * 3600 +
      parseInt(m[2], 10) * 60 +
      parseInt(m[3], 10)
    );
  }
  // formato "HH:MM"
  const m2 = s.match(/^(\d+):(\d+)$/);
  if (m2) return parseInt(m2[1], 10) * 3600 + parseInt(m2[2], 10) * 60;
  return 0;
}

function parseOperator(raw) {
  // formatos esperados:
  //   "001028 - 1028 - JEANE GUEDES RIBEIRO"
  //   "000033 - ANDREIA APARECIDA RIBEIRO"
  const str = String(raw || "").replace(/\s+/g, " ").trim();
  const parts = str.split(/\s+-\s+/);
  if (parts.length >= 3) {
    return {
      codigo: parts[0],
      numero: Number(parts[1]) || parts[1],
      nome: parts.slice(2).join(" - "),
    };
  }
  if (parts.length === 2) {
    return {
      codigo: parts[0],
      numero: Number(parts[0]) || parts[0],
      nome: parts[1],
    };
  }
  return { codigo: "", numero: "", nome: str };
}

function normalize(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim();
}

function findHeaderRow(rows) {
  // procura a primeira linha que contém uma célula com "operador"
  for (let i = 0; i < Math.min(rows.length, 30); i++) {
    const row = rows[i] || [];
    for (const cell of row) {
      if (normalize(cell).includes("operador")) return i;
    }
  }
  return -1;
}

function mapColumns(headerRow) {
  // Mapeia índice de coluna → key interna
  const result = {};
  headerRow.forEach((cell, idx) => {
    const norm = normalize(cell);
    if (!norm) return;
    for (const { key, patterns } of COLUMN_MAP) {
      // já mapeado, evita sobrescrever (ex: "min" vs "media itens")
      if (result[key] != null) continue;
      const matches = patterns.some((p) => norm.includes(p));
      if (matches) {
        // anti-falso-positivo: "media itens" não deve ser pego como "itens" via "nroItens"
        if (key === "nroItens" && norm.includes("media")) continue;
        if (key === "minVendido" && norm.includes("media")) continue;
        if (key === "maxVendido" && norm.includes("media")) continue;
        result[key] = idx;
        break;
      }
    }
  });
  return result;
}

function extractMetadata(metaRows) {
  const flat = metaRows
    .flat()
    .filter((c) => c != null && String(c).trim() !== "")
    .map((c) => String(c).trim());

  const meta = {};
  for (const cell of flat) {
    const norm = normalize(cell);

    // Período: "DD/MM/YYYY a DD/MM/YYYY"
    const periodMatch = cell.match(
      /(\d{2}\/\d{2}\/\d{4})\s*a\s*(\d{2}\/\d{2}\/\d{4})/
    );
    if (periodMatch && !meta.periodoInicio) {
      meta.periodoInicio = periodMatch[1];
      meta.periodoFim = periodMatch[2];
    }

    // Razão: "Razão: NOME"
    const razaoMatch = cell.match(/raz[ãa]o[:\s]+(.+)/i);
    if (razaoMatch && !meta.razao) {
      meta.razao = razaoMatch[1].trim();
    }

    // CNPJ: "CNPJ: XX.XXX.XXX/XXXX-XX"
    const cnpjMatch = cell.match(
      /(\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2})/
    );
    if (cnpjMatch && !meta.cnpj) meta.cnpj = cnpjMatch[1];

    // Loja: "Loja: 001"
    const lojaMatch = cell.match(/loja[:\s]+(\d+)/i);
    if (lojaMatch && !meta.loja) meta.loja = lojaMatch[1];

    // Emissão: "DD/MM/YYYY - HH:mm"
    if (norm.includes("emiss")) {
      // pode estar em outra célula adjacente
      meta.emissaoBruta = cell;
    }
    const emissaoMatch = cell.match(
      /(\d{2}\/\d{2}\/\d{4})\s*[-–]\s*(\d{1,2}:\d{2})/
    );
    if (emissaoMatch && !meta.emissaoData) {
      meta.emissaoData = emissaoMatch[1];
      meta.emissaoHora = emissaoMatch[2];
    }
  }
  return meta;
}

// Processa as linhas já normalizadas (array 2D de células texto) — usado
// tanto pelo parser HTML quanto pelo XLSX.
function processRows(rows) {
  const headerIdx = findHeaderRow(rows);
  if (headerIdx === -1) {
    throw new Error(
      'Não foi possível localizar a linha de cabeçalho (esperado coluna "Operador(a)").'
    );
  }

  const metaRows = rows.slice(0, headerIdx);
  const headerRow = rows[headerIdx];
  const dataRows = rows.slice(headerIdx + 1);
  const colMap = mapColumns(headerRow);

  if (colMap.operadorRaw == null) {
    throw new Error("Coluna de operador não encontrada no cabeçalho.");
  }

  const operadores = [];
  for (const row of dataRows) {
    const rawOperator = row[colMap.operadorRaw];
    if (!rawOperator || !String(rawOperator).trim()) continue;

    // pula linhas de totais/rodapé
    const norm = normalize(rawOperator);
    if (norm.startsWith("total") || norm.startsWith("subtotal")) continue;

    const op = parseOperator(rawOperator);
    if (!op.nome) continue;

    const get = (key) => {
      const idx = colMap[key];
      return idx == null ? null : row[idx];
    };

    const aberturas = parseInteger(get("aberturas"));
    const tempoVenda = parseTime(get("tempoVenda"));
    const tempoRegistro = parseTime(get("tempoRegistro"));
    const tempoPagto = parseTime(get("tempoPagto"));
    const tempoPausa = parseTime(get("tempoPausa"));
    const qtdClientes = parseInteger(get("qtdClientes"));
    const valorTotal = parseDecimal(get("valorTotal"));
    const nroItens = parseInteger(get("nroItens"));
    const ticketMedio = parseDecimal(get("ticketMedio"));
    const minVendido = parseDecimal(get("minVendido"));
    const maxVendido = parseDecimal(get("maxVendido"));
    const mediaItens = parseDecimal(get("mediaItens"));
    const regMin = parseDecimal(get("regMin"));

    operadores.push({
      ...op,
      aberturas,
      tempoVenda,
      tempoRegistro,
      tempoPagto,
      tempoPausa,
      qtdClientes,
      valorTotal,
      nroItens,
      ticketMedio: ticketMedio || (qtdClientes > 0 ? valorTotal / qtdClientes : 0),
      minVendido,
      maxVendido,
      mediaItens: mediaItens || (qtdClientes > 0 ? nroItens / qtdClientes : 0),
      regMin,
      pontuacao: calcularPontuacao(nroItens, qtdClientes),
    });
  }

  const metadata = extractMetadata(metaRows);
  return { metadata, operadores };
}

// Parser para arquivos XLS/XLSX reais (binários).
function parseWorkbook(arrayBuffer) {
  const workbook = XLSX.read(arrayBuffer, { type: "array" });
  const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!firstSheet) throw new Error("Planilha vazia ou sem abas.");

  const rows = XLSX.utils.sheet_to_json(firstSheet, {
    header: 1,
    raw: false,
    defval: "",
  });
  return processRows(rows);
}

// Parser para o relatório legado: HTML disfarçado de .xls
// (Content-Type text/html, codificação ISO-8859-1). O sistema gerador grava
// a tabela como HTML e renomeia para .xls para abrir no Excel. Não é XLS de
// verdade — o xlsx não consegue interpretar os números BR corretamente.
function parseHtmlReport(htmlText) {
  if (typeof DOMParser === "undefined") {
    throw new Error("DOMParser não disponível neste ambiente.");
  }
  const doc = new DOMParser().parseFromString(htmlText, "text/html");
  // O HTML tem tabela aninhada: uma <table> wrapper com 1 célula que envolve
  // a <table> interna com os dados. Sem o filtro, o <tr> wrapper apareceria
  // como "linha gigante" antes do conteúdo real e quebraria findHeaderRow.
  const trs = Array.from(doc.querySelectorAll("tr")).filter(
    (tr) => !tr.querySelector("table")
  );
  if (!trs.length) {
    throw new Error("Nenhuma linha de tabela encontrada no relatório HTML.");
  }

  // Expande colspan: cada <td colspan=N> ocupa N posições. A 1ª recebe o texto,
  // as N-1 seguintes ficam vazias. Isso preserva o alinhamento das colunas com
  // o cabeçalho (que também usa colspan=2 em "Operador(a)").
  const rows = trs.map((tr) => {
    const cells = Array.from(tr.querySelectorAll("th, td"));
    const flat = [];
    cells.forEach((cell) => {
      const colspan = parseInt(cell.getAttribute("colspan") || "1", 10) || 1;
      const text = (cell.textContent || "")
        .replace(/ /g, " ")
        .replace(/\s+/g, " ")
        .trim();
      flat.push(text);
      for (let i = 1; i < colspan; i++) flat.push("");
    });
    return flat;
  });

  return processRows(rows);
}

// Detecta se o conteúdo é HTML inspecionando os primeiros bytes.
function isHtmlBuffer(buffer) {
  const previewLen = Math.min(2048, buffer.byteLength);
  const preview = new TextDecoder("iso-8859-1").decode(
    buffer.slice(0, previewLen)
  );
  return /<\s*(html|table|tr|td|body)\b/i.test(preview);
}

function parseFileBuffer(arrayBuffer) {
  if (isHtmlBuffer(arrayBuffer)) {
    // Tenta ISO-8859-1 (legado BR) primeiro; se houver caracteres de troca,
    // refaz com UTF-8.
    const latin1 = new TextDecoder("iso-8859-1").decode(arrayBuffer);
    return parseHtmlReport(latin1);
  }
  return parseWorkbook(arrayBuffer);
}

// ─────────────────────────────────────────────────────────────────────────────
// Agregação
// ─────────────────────────────────────────────────────────────────────────────
function aggregate(operadores) {
  const totals = {
    aberturas: 0,
    tempoVenda: 0,
    tempoRegistro: 0,
    tempoPagto: 0,
    tempoPausa: 0,
    qtdClientes: 0,
    valorTotal: 0,
    nroItens: 0,
  };

  for (const o of operadores) {
    totals.aberturas += o.aberturas;
    totals.tempoVenda += o.tempoVenda;
    totals.tempoRegistro += o.tempoRegistro;
    totals.tempoPagto += o.tempoPagto;
    totals.tempoPausa += o.tempoPausa;
    totals.qtdClientes += o.qtdClientes;
    totals.valorTotal += o.valorTotal;
    totals.nroItens += o.nroItens;
  }

  const ativos = operadores.filter((o) => o.qtdClientes > 0);

  totals.ticketMedio =
    totals.qtdClientes > 0 ? totals.valorTotal / totals.qtdClientes : 0;
  totals.itensPorCliente =
    totals.qtdClientes > 0 ? totals.nroItens / totals.qtdClientes : 0;
  totals.regMinMedia =
    ativos.length > 0
      ? ativos.reduce((s, o) => s + o.regMin, 0) / ativos.length
      : 0;
  totals.operadoresAtivos = ativos.length;
  totals.operadoresTotal = operadores.length;

  // Top performers por diferentes métricas. O ranking principal é por
  // PONTUAÇÃO (1 ponto por item + 2 pontos por cliente) — mede o esforço real
  // da operadora no caixa sem premiar só cupom grande. Em empate, desempata
  // pela quantidade de itens e, persistindo, pelo valor total.
  const byPontuacao = [...operadores].sort(
    (a, b) =>
      b.pontuacao - a.pontuacao ||
      b.nroItens - a.nroItens ||
      b.valorTotal - a.valorTotal
  );
  const byProdutividade = [...operadores].sort((a, b) => b.regMin - a.regMin);
  const byClientes = [...operadores].sort((a, b) => b.qtdClientes - a.qtdClientes);

  return {
    totals,
    ranking: byPontuacao,
    topProdutividade: byProdutividade.find((o) => o.regMin > 0) || null,
    topClientes: byClientes.find((o) => o.qtdClientes > 0) || null,
    topTicket: [...operadores]
      .filter((o) => o.qtdClientes > 0)
      .sort((a, b) => b.ticketMedio - a.ticketMedio)[0] || null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Comparação entre dois relatórios
// ─────────────────────────────────────────────────────────────────────────────
function deltaState(diff) {
  if (!diff || diff === 0) return "flat";
  return diff > 0 ? "up" : "down";
}

function buildComparison(currentAgg, compareAgg) {
  // Une operadores das duas planilhas pelo numero (caixa que existe só num
  // dos arquivos entra com o lado faltante zerado).
  const byNumero = new Map();
  const ensure = (op) => {
    if (!byNumero.has(op.numero)) {
      byNumero.set(op.numero, {
        numero: op.numero,
        nome: op.nome,
        curr: null,
        prev: null,
      });
    }
    return byNumero.get(op.numero);
  };
  for (const op of currentAgg.ranking) ensure(op).curr = op;
  for (const op of compareAgg.ranking) {
    const entry = ensure(op);
    entry.prev = op;
    if (!entry.nome) entry.nome = op.nome;
  }

  const rows = Array.from(byNumero.values()).map((entry) => {
    const curr = entry.curr || {};
    const prev = entry.prev || {};
    const diff = (a, b) => (a || 0) - (b || 0);
    return {
      numero: entry.numero,
      nome: entry.nome || `Operador ${entry.numero}`,
      curr: entry.curr,
      prev: entry.prev,
      deltaValor: diff(curr.valorTotal, prev.valorTotal),
      deltaClientes: diff(curr.qtdClientes, prev.qtdClientes),
      deltaTicket: diff(curr.ticketMedio, prev.ticketMedio),
      deltaRegMin: diff(curr.regMin, prev.regMin),
      deltaAberturas: diff(curr.aberturas, prev.aberturas),
      deltaItens: diff(curr.nroItens, prev.nroItens),
      deltaTempoVenda: diff(curr.tempoVenda, prev.tempoVenda),
      deltaTempoRegistro: diff(curr.tempoRegistro, prev.tempoRegistro),
      deltaTempoPagto: diff(curr.tempoPagto, prev.tempoPagto),
    };
  });

  const totals = {
    deltaValor: currentAgg.totals.valorTotal - compareAgg.totals.valorTotal,
    deltaClientes:
      currentAgg.totals.qtdClientes - compareAgg.totals.qtdClientes,
    deltaTicket: currentAgg.totals.ticketMedio - compareAgg.totals.ticketMedio,
    deltaRegMin:
      currentAgg.totals.regMinMedia - compareAgg.totals.regMinMedia,
    deltaAberturas:
      currentAgg.totals.aberturas - compareAgg.totals.aberturas,
    deltaItens: currentAgg.totals.nroItens - compareAgg.totals.nroItens,
    deltaTempoVenda:
      currentAgg.totals.tempoVenda - compareAgg.totals.tempoVenda,
    deltaTempoRegistro:
      currentAgg.totals.tempoRegistro - compareAgg.totals.tempoRegistro,
    deltaTempoPagto:
      currentAgg.totals.tempoPagto - compareAgg.totals.tempoPagto,
  };

  return { rows, totals };
}

// ─────────────────────────────────────────────────────────────────────────────
// Componentes
// ─────────────────────────────────────────────────────────────────────────────
function StatusPill({ fileName }) {
  return (
    <div style={header.pill} title={fileName}>
      <span style={header.pillDot} />
      <span>
        {fileName ? `ARQUIVO · ${fileName}` : "AGUARDANDO ARQUIVO"}
      </span>
    </div>
  );
}

function KpiCard({ idx, label, value, unit, hint }) {
  return (
    <div style={kpis.card(idx)}>
      <div style={kpis.label}>{label}</div>
      <div style={kpis.value}>
        {value}
        {unit && <span style={kpis.unit}>{unit}</span>}
      </div>
      {hint && <div style={kpis.hint}>{hint}</div>}
    </div>
  );
}

function PodiumSpot({ place, operator, hideMoney }) {
  const fmtMoney = hideMoney ? maskCurrency : fmtCurrency;
  if (!operator) {
    return (
      <div style={podium.spot(place)}>
        <img src={MEDALS[place]} alt="" style={podium.medal(place)} />
        <div style={podium.placeTag(place)}>{PLACE_LABELS[place]}</div>
        <div style={{ ...podium.name(place), color: colors.textSoft }}>
          Sem dados
        </div>
      </div>
    );
  }
  return (
    <div style={podium.spot(place)}>
      <img src={MEDALS[place]} alt="" style={podium.medal(place)} />
      <div style={podium.placeTag(place)}>{PLACE_LABELS[place]}</div>
      <div style={podium.name(place)}>{operator.nome}</div>
      <div style={podium.operator}>OP #{operator.numero}</div>
      <div style={podium.metrics}>
        <div style={podium.metricRow}>
          <span style={podium.metricLabel}>Pontos</span>
          <span style={{ ...podium.metricValue, fontWeight: 800 }}>
            {fmtNumber(operator.pontuacao)}
          </span>
        </div>
        <div style={podium.metricRow}>
          <span style={podium.metricLabel}>Itens</span>
          <span style={podium.metricValue}>
            {fmtNumber(operator.nroItens)}
          </span>
        </div>
        <div style={podium.metricRow}>
          <span style={podium.metricLabel}>Clientes</span>
          <span style={podium.metricValue}>
            {fmtNumber(operator.qtdClientes)}
          </span>
        </div>
        <div style={podium.metricRow}>
          <span style={podium.metricLabel}>Valor</span>
          <span style={podium.metricValue}>
            {fmtMoney(operator.valorTotal)}
          </span>
        </div>
      </div>
    </div>
  );
}

function PodiumPanel({ ranking, hideMoney }) {
  const hasAnyPoints = ranking.some((o) => o.pontuacao > 0);
  return (
    <section style={podium.wrap}>
      <div style={podium.head}>
        <h2 style={podium.title}>PÓDIO DE PERFORMANCE</h2>
        <span style={podium.subtitle}>
          Top 3 operadores por pontuação — 1 ponto por item + 2 por cliente
        </span>
      </div>
      {!hasAnyPoints ? (
        <div style={podium.empty}>
          Nenhum operador com movimentação no relatório.
        </div>
      ) : (
        <div style={podium.grid}>
          <PodiumSpot place={2} operator={ranking[1]} hideMoney={hideMoney} />
          <PodiumSpot place={1} operator={ranking[0]} hideMoney={hideMoney} />
          <PodiumSpot place={3} operator={ranking[2]} hideMoney={hideMoney} />
        </div>
      )}
    </section>
  );
}

function MetaPanel({ metadata, fileName }) {
  const items = [
    { label: "Período", value: metadata.periodoInicio
      ? `${metadata.periodoInicio} a ${metadata.periodoFim}`
      : "—" },
    { label: "Loja", value: metadata.loja || "—" },
    { label: "Razão social", value: metadata.razao || "—" },
    { label: "CNPJ", value: metadata.cnpj || "—" },
    {
      label: "Emissão",
      value:
        metadata.emissaoData
          ? `${metadata.emissaoData} ${metadata.emissaoHora || ""}`.trim()
          : "—",
    },
    { label: "Arquivo", value: fileName || "—" },
  ];

  return (
    <section style={meta.wrap}>
      <div style={meta.grid}>
        {items.map((it) => (
          <div key={it.label} style={meta.item}>
            <div style={meta.label}>{it.label}</div>
            <div style={meta.value}>{it.value}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ProductivityTable({ ranking, hideMoney }) {
  const fmtMoney = hideMoney ? maskCurrency : fmtCurrency;
  const ativos = ranking.filter((o) => o.qtdClientes > 0).length;

  return (
    <section style={tableSection.wrap}>
      <div style={tableSection.head}>
        <h2 style={tableSection.title}>RANKING COMPLETO</h2>
        <span style={tableSection.count}>
          {fmtNumber(ranking.length)} operador(es) ·{" "}
          {fmtNumber(ativos)} com movimentação
        </span>
      </div>
      <div style={tableSection.scroll}>
        {ranking.length === 0 ? (
          <div style={tableSection.empty}>Nenhum operador encontrado.</div>
        ) : (
          <table style={tableSection.table}>
            <thead>
              <tr>
                <th style={tableSection.th}>#</th>
                <th style={tableSection.th}>Operador</th>
                <th style={tableSection.th}>Nº</th>
                <th
                  style={{ ...tableSection.th, ...tableSection.thRight }}
                  title="1 ponto por item registrado + 2 pontos por cliente atendido"
                >
                  Pontos
                </th>
                <th style={{ ...tableSection.th, ...tableSection.thRight }}>
                  Aberturas
                </th>
                <th style={{ ...tableSection.th, ...tableSection.thRight }}>
                  Tempo Venda
                </th>
                <th style={{ ...tableSection.th, ...tableSection.thRight }}>
                  Tempo Registro
                </th>
                <th style={{ ...tableSection.th, ...tableSection.thRight }}>
                  Tempo Pagto
                </th>
                <th style={{ ...tableSection.th, ...tableSection.thRight }}>
                  Clientes
                </th>
                <th style={{ ...tableSection.th, ...tableSection.thRight }}>
                  Itens
                </th>
                <th style={{ ...tableSection.th, ...tableSection.thRight }}>
                  Média Itens
                </th>
                <th style={{ ...tableSection.th, ...tableSection.thRight }}>
                  Ticket Médio
                </th>
                <th style={{ ...tableSection.th, ...tableSection.thRight }}>
                  Min/Max
                </th>
                <th style={{ ...tableSection.th, ...tableSection.thRight }}>
                  Reg/min
                </th>
                <th style={{ ...tableSection.th, ...tableSection.thRight }}>
                  Valor Total
                </th>
              </tr>
            </thead>
            <tbody>
              {ranking.map((op, i) => {
                const place = i + 1;
                return (
                  <tr key={`${op.codigo}-${i}`} style={tableSection.tr(i)}>
                    <td style={tableSection.td}>
                      <span style={tableSection.rank(place)}>{place}º</span>
                    </td>
                    <td
                      style={{
                        ...tableSection.td,
                        whiteSpace: "normal",
                        fontWeight: 600,
                      }}
                    >
                      {op.nome}
                    </td>
                    <td
                      style={{
                        ...tableSection.td,
                        fontFamily: "'Geist Mono', monospace",
                        color: colors.textSoft,
                      }}
                    >
                      {op.numero}
                    </td>
                    <td
                      style={{
                        ...tableSection.td,
                        ...tableSection.tdRight,
                        fontWeight: 700,
                      }}
                      title={`${fmtNumber(op.nroItens)} itens + ${fmtNumber(op.qtdClientes)} clientes × ${PONTOS_POR_CLIENTE}`}
                    >
                      {fmtNumber(op.pontuacao)}
                    </td>
                    <td style={{ ...tableSection.td, ...tableSection.tdRight }}>
                      {fmtNumber(op.aberturas)}
                    </td>
                    <td style={{ ...tableSection.td, ...tableSection.tdRight }}>
                      {fmtSeconds(op.tempoVenda)}
                    </td>
                    <td style={{ ...tableSection.td, ...tableSection.tdRight }}>
                      {fmtSeconds(op.tempoRegistro)}
                    </td>
                    <td style={{ ...tableSection.td, ...tableSection.tdRight }}>
                      {fmtSeconds(op.tempoPagto)}
                    </td>
                    <td style={{ ...tableSection.td, ...tableSection.tdRight }}>
                      {fmtNumber(op.qtdClientes)}
                    </td>
                    <td style={{ ...tableSection.td, ...tableSection.tdRight }}>
                      {fmtNumber(op.nroItens)}
                    </td>
                    <td style={{ ...tableSection.td, ...tableSection.tdRight }}>
                      {fmtDecimal(op.mediaItens, 1)}
                    </td>
                    <td style={{ ...tableSection.td, ...tableSection.tdRight }}>
                      {fmtMoney(op.ticketMedio)}
                    </td>
                    <td
                      style={{
                        ...tableSection.td,
                        ...tableSection.tdRight,
                        color: colors.textSoft,
                        fontSize: 12.5,
                      }}
                    >
                      {fmtNumber(op.minVendido)} / {fmtNumber(op.maxVendido)}
                    </td>
                    <td style={{ ...tableSection.td, ...tableSection.tdRight }}>
                      {fmtDecimal(op.regMin, 2)}
                    </td>
                    <td style={{ ...tableSection.td, ...tableSection.tdRight }}>
                      {fmtMoney(op.valorTotal)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function SummaryPanel({ totals, topProdutividade, topClientes, topTicket, hideMoney }) {
  const fmtMoney = hideMoney ? maskCurrency : fmtCurrency;
  const tempoTotal = totals.tempoVenda + totals.tempoRegistro + totals.tempoPagto;
  const items = [
    {
      label: "Mais produtivo (Reg/min)",
      value: topProdutividade
        ? `${topProdutividade.nome} · ${fmtDecimal(topProdutividade.regMin, 2)} reg/min`
        : "Sem dados",
    },
    {
      label: "Mais clientes atendidos",
      value: topClientes
        ? `${topClientes.nome} · ${fmtNumber(topClientes.qtdClientes)} clientes`
        : "Sem dados",
    },
    {
      label: "Maior ticket médio",
      value: topTicket
        ? `${topTicket.nome} · ${fmtMoney(topTicket.ticketMedio)}`
        : "Sem dados",
    },
    {
      label: "Tempo total operando",
      value: fmtSeconds(tempoTotal),
    },
    {
      label: "Tempo em pausa (acumulado)",
      value:
        totals.tempoPausa > 0 ? fmtSeconds(totals.tempoPausa) : "00:00:00",
    },
    {
      label: "Itens por cliente (média geral)",
      value: fmtDecimal(totals.itensPorCliente, 1),
    },
  ];

  return (
    <section style={summary.wrap}>
      <h3 style={summary.title}>DESTAQUES PARA TOMADA DE DECISÃO</h3>
      <div style={summary.list}>
        {items.map((it) => (
          <div key={it.label} style={summary.item}>
            <div style={summary.itemLabel}>{it.label}</div>
            <div style={summary.itemValue}>{it.value}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function UploadArea({ onFile, error, parsing, dragOver, onDragOver, onDragLeave, onDrop, onClick }) {
  return (
    <section
      style={upload.wrap(dragOver)}
      onClick={onClick}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div style={upload.icon}>📊</div>
      <div style={upload.title}>
        {parsing ? "Processando arquivo…" : "Carregar relatório de produtividade"}
      </div>
      <div style={upload.subtitle}>
        Arraste e solte aqui ou clique para selecionar o arquivo XLS exportado
        do sistema.
      </div>
      <span style={upload.cta}>Selecionar arquivo</span>
      <div style={upload.formats}>Formatos aceitos: .xls · .xlsx · .xlsm</div>
      {error && <div style={upload.error}>⚠ {error}</div>}
    </section>
  );
}

function CompareUploadArea({
  parsing,
  error,
  dragOver,
  onClick,
  onDragOver,
  onDragLeave,
  onDrop,
}) {
  return (
    <section
      style={compareUpload.wrap(dragOver, error)}
      onClick={onClick}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div style={compareUpload.text}>
        <span style={compareUpload.title}>
          {parsing
            ? "Processando arquivo de comparação…"
            : "Adicionar relatório de comparação"}
        </span>
        <span style={compareUpload.subtitle}>
          Suba um segundo XLS para gerar o gráfico de diferenças por operador.
          Arraste e solte aqui ou clique no botão.
        </span>
      </div>
      <button
        type="button"
        style={compareUpload.cta}
        onClick={(e) => {
          // O <section> pai também tem onClick={onClick} para permitir click
          // em qualquer lugar do banner. Sem stopPropagation, o click no botão
          // dispara o handler duas vezes (botão + bubble pro section) e abre
          // o file picker em sequência.
          e.stopPropagation();
          onClick();
        }}
      >
        Selecionar arquivo
      </button>
      {error && <div style={compareUpload.error}>⚠ {error}</div>}
    </section>
  );
}

const COMPARE_METRICS = [
  {
    key: "valor",
    label: "Valor Total",
    deltaKey: "deltaValor",
    formatter: fmtCurrency,
  },
  {
    key: "clientes",
    label: "Clientes",
    deltaKey: "deltaClientes",
    formatter: fmtNumber,
  },
  {
    key: "ticket",
    label: "Ticket Médio",
    deltaKey: "deltaTicket",
    formatter: fmtCurrency,
  },
  {
    key: "regMin",
    label: "Reg/Min",
    deltaKey: "deltaRegMin",
    formatter: (v) => fmtDecimal(v, 2),
  },
  {
    key: "aberturas",
    label: "Aberturas",
    deltaKey: "deltaAberturas",
    formatter: fmtNumber,
  },
  {
    key: "itens",
    label: "Itens",
    deltaKey: "deltaItens",
    formatter: fmtNumber,
  },
  {
    key: "tempoVenda",
    label: "Tempo Venda",
    deltaKey: "deltaTempoVenda",
    formatter: fmtSignedSeconds,
  },
  {
    key: "tempoRegistro",
    label: "Tempo Registro",
    deltaKey: "deltaTempoRegistro",
    formatter: fmtSignedSeconds,
  },
  {
    key: "tempoPagto",
    label: "Tempo Pagto",
    deltaKey: "deltaTempoPagto",
    formatter: fmtSignedSeconds,
  },
];

function ComparisonPanel({
  comparisonData,
  currentLabel,
  compareLabel,
  hideMoney,
}) {
  const [metric, setMetric] = useState("valor");
  const [selectedOps, setSelectedOps] = useState(() => new Set());
  const [searchQuery, setSearchQuery] = useState("");

  // Quando o usuário pediu para mascarar valores monetários, sobrescreve o
  // formatter dos métricos `valor` e `ticket` (os únicos em R$).
  const compareMetrics = useMemo(() => {
    if (!hideMoney) return COMPARE_METRICS;
    return COMPARE_METRICS.map((m) =>
      m.formatter === fmtCurrency ? { ...m, formatter: maskCurrency } : m
    );
  }, [hideMoney]);

  const cfg =
    compareMetrics.find((m) => m.key === metric) || compareMetrics[0];

  const allRows = comparisonData.rows;
  const isFiltered = selectedOps.size > 0;

  // Linhas após filtro de seleção (todas se nada estiver selecionado)
  const filteredByOp = useMemo(() => {
    if (!isFiltered) return allRows;
    return allRows.filter((r) => selectedOps.has(r.numero));
  }, [allRows, selectedOps, isFiltered]);

  // Chips visíveis: todos os operadores, com busca por nome ou número
  const visibleChips = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const sorted = [...allRows].sort((a, b) =>
      String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR")
    );
    if (!q) return sorted;
    return sorted.filter(
      (op) =>
        String(op.nome).toLowerCase().includes(q) ||
        String(op.numero).includes(q)
    );
  }, [allRows, searchQuery]);

  const toggleOp = (numero) => {
    setSelectedOps((prev) => {
      const next = new Set(prev);
      if (next.has(numero)) next.delete(numero);
      else next.add(numero);
      return next;
    });
  };

  const clearSelection = () => setSelectedOps(new Set());

  const sortedRows = useMemo(() => {
    return [...filteredByOp]
      .filter((r) => r[cfg.deltaKey] !== 0 || r.curr || r.prev)
      .sort(
        (a, b) => Math.abs(b[cfg.deltaKey]) - Math.abs(a[cfg.deltaKey])
      );
  }, [filteredByOp, cfg.deltaKey]);

  const maxAbs = sortedRows.length
    ? Math.max(...sortedRows.map((r) => Math.abs(r[cfg.deltaKey])))
    : 0;

  // Totais agregados respeitam a seleção: se 1+ operador selecionado, somam só
  // os deltas desses operadores. Sem seleção, usam os totais globais já calculados.
  const aggregatedDeltas = useMemo(() => {
    if (!isFiltered) {
      return {
        deltaValor: comparisonData.totals.deltaValor,
        deltaClientes: comparisonData.totals.deltaClientes,
        deltaTicket: comparisonData.totals.deltaTicket,
        deltaRegMin: comparisonData.totals.deltaRegMin,
        deltaAberturas: comparisonData.totals.deltaAberturas,
        deltaItens: comparisonData.totals.deltaItens,
        deltaTempoVenda: comparisonData.totals.deltaTempoVenda,
        deltaTempoRegistro: comparisonData.totals.deltaTempoRegistro,
        deltaTempoPagto: comparisonData.totals.deltaTempoPagto,
      };
    }
    // Para Ticket Médio e Reg/Min, somar deltas individuais não faz sentido —
    // melhor usar a média dos deltas dos operadores selecionados.
    const sum = (key) =>
      filteredByOp.reduce((s, r) => s + (r[key] || 0), 0);
    const avg = (key) => {
      const filtered = filteredByOp.filter((r) => r.curr || r.prev);
      return filtered.length ? sum(key) / filtered.length : 0;
    };
    return {
      deltaValor: sum("deltaValor"),
      deltaClientes: sum("deltaClientes"),
      deltaTicket: avg("deltaTicket"),
      deltaRegMin: avg("deltaRegMin"),
      deltaAberturas: sum("deltaAberturas"),
      deltaItens: sum("deltaItens"),
      deltaTempoVenda: sum("deltaTempoVenda"),
      deltaTempoRegistro: sum("deltaTempoRegistro"),
      deltaTempoPagto: sum("deltaTempoPagto"),
    };
  }, [isFiltered, filteredByOp, comparisonData.totals]);

  const moneyFmt = hideMoney ? maskCurrency : fmtCurrency;
  const totalCards = [
    { label: "Valor total", delta: aggregatedDeltas.deltaValor, formatter: moneyFmt },
    { label: "Clientes", delta: aggregatedDeltas.deltaClientes, formatter: fmtNumber },
    { label: "Ticket médio", delta: aggregatedDeltas.deltaTicket, formatter: moneyFmt },
    { label: "Reg/Min médio", delta: aggregatedDeltas.deltaRegMin, formatter: (v) => fmtDecimal(v, 2) },
    { label: "Aberturas", delta: aggregatedDeltas.deltaAberturas, formatter: fmtNumber },
    { label: "Itens", delta: aggregatedDeltas.deltaItens, formatter: fmtNumber },
    { label: "Tempo venda", delta: aggregatedDeltas.deltaTempoVenda, formatter: fmtSignedSeconds },
    { label: "Tempo registro", delta: aggregatedDeltas.deltaTempoRegistro, formatter: fmtSignedSeconds },
    { label: "Tempo pagto", delta: aggregatedDeltas.deltaTempoPagto, formatter: fmtSignedSeconds },
  ];

  return (
    <section style={comparison.wrap}>
      <div style={comparison.head}>
        <h2 style={comparison.title}>COMPARAÇÃO ENTRE PERÍODOS</h2>
        <span style={comparison.subtitle}>
          {currentLabel} vs {compareLabel}
        </span>
      </div>

      {/* FILTRO POR OPERADOR */}
      <div style={comparison.filterBlock}>
        <div style={comparison.filterHeader}>
          <div style={comparison.filterTitleGroup}>
            <span style={comparison.filterTitle}>Filtrar operador</span>
            <span
              style={
                isFiltered
                  ? comparison.filterStatusActive
                  : comparison.filterStatus
              }
            >
              {isFiltered
                ? `${selectedOps.size} selecionado${selectedOps.size > 1 ? "s" : ""} de ${allRows.length}`
                : `Todos os ${allRows.length} operadores`}
            </span>
          </div>
          <div style={comparison.filterControls}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por nome ou nº…"
              style={comparison.searchInput}
            />
            {isFiltered && (
              <button
                type="button"
                onClick={clearSelection}
                style={comparison.clearBtn}
              >
                Limpar seleção
              </button>
            )}
          </div>
        </div>
        <div style={comparison.chipList}>
          {visibleChips.length === 0 ? (
            <span style={comparison.chipEmpty}>
              Nenhum operador encontrado para "{searchQuery}".
            </span>
          ) : (
            visibleChips.map((op) => (
              <button
                key={op.numero}
                type="button"
                onClick={() => toggleOp(op.numero)}
                style={comparison.chip(selectedOps.has(op.numero))}
              >
                {op.nome}
                <span style={comparison.chipNumero}>#{op.numero}</span>
              </button>
            ))
          )}
        </div>
      </div>

      <div style={comparison.totalsRow}>
        {totalCards.map((tc) => {
          const state = deltaState(tc.delta);
          const sign = tc.delta > 0 ? "+" : "";
          return (
            <div key={tc.label} style={comparison.totalCard(state)}>
              <span style={comparison.totalLabel}>{tc.label}</span>
              <span style={comparison.totalValue(state)}>
                {sign}
                {tc.formatter(tc.delta)}
              </span>
            </div>
          );
        })}
      </div>

      <div style={comparison.tabs}>
        {compareMetrics.map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => setMetric(m.key)}
            style={comparison.tab(metric === m.key)}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div style={comparison.legend}>
        <div style={comparison.legendItem}>
          <span style={comparison.legendSwatch(colors.success)} />
          Aumento (atual &gt; comparação)
        </div>
        <div style={comparison.legendItem}>
          <span style={comparison.legendSwatch(colors.brand)} />
          Queda (atual &lt; comparação)
        </div>
      </div>

      {sortedRows.length === 0 ? (
        <div style={comparison.empty}>
          Nenhuma diferença para os operadores nesta métrica.
        </div>
      ) : (
        <div style={{ ...comparison.chart, marginTop: 14 }}>
          {sortedRows.map((row) => {
            const delta = row[cfg.deltaKey];
            const positive = delta > 0;
            const pct = maxAbs > 0 ? (Math.abs(delta) / maxAbs) * 100 : 0;
            const state = deltaState(delta);
            const sign = delta > 0 ? "+" : "";
            return (
              <div key={row.numero} style={comparison.row}>
                <span style={comparison.rowName}>
                  {row.nome}
                  <span style={comparison.rowNumero}>#{row.numero}</span>
                </span>
                <div style={comparison.barTrack}>
                  <div style={comparison.barHalf("left")}>
                    {!positive && delta !== 0 && (
                      <div style={comparison.barFill(false, pct)} />
                    )}
                  </div>
                  <div style={comparison.barAxis} />
                  <div style={comparison.barHalf("right")}>
                    {positive && (
                      <div style={comparison.barFill(true, pct)} />
                    )}
                  </div>
                </div>
                <span style={comparison.rowDelta(state)}>
                  {sign}
                  {cfg.formatter(delta)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Página
// ─────────────────────────────────────────────────────────────────────────────
function Productivity(props) {
  const {
    location: { idPage },
  } = props;

  const [auth, setAuth] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [hideMoney, setHideMoney] = useState(false);
  const fileInputRef = useRef(null);

  const fmtMoney = hideMoney ? maskCurrency : fmtCurrency;

  // Estado do segundo relatório (comparação)
  const [compareFileName, setCompareFileName] = useState(null);
  const [compareParsing, setCompareParsing] = useState(false);
  const [compareError, setCompareError] = useState(null);
  const [compareParsed, setCompareParsed] = useState(null);
  const [compareDragOver, setCompareDragOver] = useState(false);
  const compareInputRef = useRef(null);

  async function verifyAuth() {
    const { profile } = store.getState().user;
    const res = await apiLabs.post("valid_key", {
      id: profile.id,
      menu_id: idPage,
    });
    if (res.data.message === "User not authorized") return setAuth("denied");
    if (res.data.message === "User authorized") return setAuth("authorized");
    return history.push({ pathname: "/", state: { from: props.location } });
  }

  useEffect(() => {
    verifyAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setError(null);
    setParsing(true);
    setFileName(file.name);

    try {
      const buffer = await file.arrayBuffer();
      const result = parseFileBuffer(buffer);
      if (!result.operadores.length) {
        throw new Error(
          "Nenhum operador encontrado no arquivo. Confirme se é o relatório correto."
        );
      }
      setParsed(result);
    } catch (err) {
      console.error("Productivity — erro ao parsear arquivo:", err);
      setError(err.message || "Erro ao ler o arquivo.");
      setParsed(null);
    } finally {
      setParsing(false);
    }
  }, []);

  const onClickUpload = () => fileInputRef.current?.click();

  const onInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = "";
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  // Handlers do arquivo de comparação
  const handleCompareFile = useCallback(async (file) => {
    if (!file) return;
    setCompareError(null);
    setCompareParsing(true);
    setCompareFileName(file.name);

    try {
      const buffer = await file.arrayBuffer();
      const result = parseFileBuffer(buffer);
      if (!result.operadores.length) {
        throw new Error(
          "Nenhum operador encontrado no arquivo de comparação."
        );
      }
      setCompareParsed(result);
    } catch (err) {
      console.error("Productivity — erro no arquivo de comparação:", err);
      setCompareError(err.message || "Erro ao ler o arquivo de comparação.");
      setCompareParsed(null);
    } finally {
      setCompareParsing(false);
    }
  }, []);

  const onClickCompareUpload = () => compareInputRef.current?.click();

  const onCompareInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleCompareFile(file);
    e.target.value = "";
  };

  const onCompareDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCompareDragOver(true);
  };
  const onCompareDragLeave = (e) => {
    e.preventDefault();
    setCompareDragOver(false);
  };
  const onCompareDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCompareDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleCompareFile(file);
  };

  const removeCompare = () => {
    setCompareFileName(null);
    setCompareParsed(null);
    setCompareError(null);
  };

  const aggregated = useMemo(
    () => (parsed ? aggregate(parsed.operadores) : null),
    [parsed]
  );

  const compareAggregated = useMemo(
    () => (compareParsed ? aggregate(compareParsed.operadores) : null),
    [compareParsed]
  );

  const comparisonData = useMemo(() => {
    if (!aggregated || !compareAggregated) return null;
    return buildComparison(aggregated, compareAggregated);
  }, [aggregated, compareAggregated]);

  return (
    <Dashboard title="Produtividade Caixas">
      {auth === "denied" && <Denied />}
      {auth === "authorized" && (
        <>
          <style>{globalCSS}</style>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xls,.xlsx,.xlsm"
            style={{ display: "none" }}
            onChange={onInputChange}
          />
          <input
            ref={compareInputRef}
            type="file"
            accept=".xls,.xlsx,.xlsm"
            style={{ display: "none" }}
            onChange={onCompareInputChange}
          />
          <div style={page}>
            <div style={dash}>
              <header style={header.wrap}>
                <div>
                  <h1 style={header.title}>PRODUTIVIDADE DE CAIXAS</h1>
                  <p style={header.subtitle}>
                    Relatório de Estatística, Tempo e Produtividade · upload de XLS
                  </p>
                </div>
                <div style={header.right}>
                  {parsed && (
                    <button
                      type="button"
                      onClick={() => setHideMoney((v) => !v)}
                      style={header.iconButton(hideMoney)}
                      title={
                        hideMoney
                          ? "Mostrar valores monetários"
                          : "Ocultar valores monetários"
                      }
                      aria-label={
                        hideMoney
                          ? "Mostrar valores monetários"
                          : "Ocultar valores monetários"
                      }
                    >
                      {hideMoney ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  )}
                  <StatusPill fileName={fileName} />
                  {parsed && (
                    <>
                      <button
                        type="button"
                        onClick={onClickUpload}
                        style={header.button("primary")}
                      >
                        Trocar arquivo
                      </button>
                      {compareParsed ? (
                        <button
                          type="button"
                          onClick={removeCompare}
                          style={header.button("ghost")}
                          title={`Comparando com: ${compareFileName}`}
                        >
                          Remover comparação
                        </button>
                      ) : null}
                    </>
                  )}
                </div>
              </header>

              {!parsed && (
                <UploadArea
                  parsing={parsing}
                  error={error}
                  dragOver={dragOver}
                  onClick={onClickUpload}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                />
              )}

              {parsed && aggregated && (
                <>
                  <section style={kpis.grid}>
                    <KpiCard
                      idx={0}
                      label="Valor total"
                      value={fmtMoney(aggregated.totals.valorTotal)}
                      hint={`${fmtNumber(aggregated.totals.qtdClientes)} clientes atendidos`}
                    />
                    <KpiCard
                      idx={1}
                      label="Ticket médio geral"
                      value={fmtMoney(aggregated.totals.ticketMedio)}
                      hint={`${fmtDecimal(aggregated.totals.itensPorCliente, 1)} itens por cliente`}
                    />
                    <KpiCard
                      idx={2}
                      label="Itens vendidos"
                      value={fmtNumber(aggregated.totals.nroItens)}
                      hint="Total de unidades nos cupons"
                    />
                    <KpiCard
                      idx={3}
                      label="Aberturas"
                      value={fmtNumber(aggregated.totals.aberturas)}
                      hint={`${fmtNumber(aggregated.totals.operadoresAtivos)} operadores ativos`}
                    />
                    <KpiCard
                      idx={4}
                      label="Tempo de venda"
                      value={fmtSeconds(aggregated.totals.tempoVenda)}
                      hint={`Pausa: ${fmtSeconds(aggregated.totals.tempoPausa)}`}
                    />
                    <KpiCard
                      idx={5}
                      label="Reg/min médio"
                      value={fmtDecimal(aggregated.totals.regMinMedia, 2)}
                      hint={
                        aggregated.topProdutividade
                          ? `Topo: ${fmtDecimal(aggregated.topProdutividade.regMin, 2)}`
                          : "Sem dados"
                      }
                    />
                  </section>

                  <PodiumPanel ranking={aggregated.ranking} hideMoney={hideMoney} />

                  <ProductivityTable ranking={aggregated.ranking} hideMoney={hideMoney} />

                  <SummaryPanel
                    totals={aggregated.totals}
                    topProdutividade={aggregated.topProdutividade}
                    topClientes={aggregated.topClientes}
                    topTicket={aggregated.topTicket}
                    hideMoney={hideMoney}
                  />

                  {comparisonData ? (
                    <ComparisonPanel
                      comparisonData={comparisonData}
                      currentLabel={fileName || "Arquivo principal"}
                      compareLabel={compareFileName || "Comparação"}
                      hideMoney={hideMoney}
                    />
                  ) : (
                    <CompareUploadArea
                      parsing={compareParsing}
                      error={compareError}
                      dragOver={compareDragOver}
                      onClick={onClickCompareUpload}
                      onDragOver={onCompareDragOver}
                      onDragLeave={onCompareDragLeave}
                      onDrop={onCompareDrop}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </Dashboard>
  );
}

export default Productivity;
